import axios, { InternalAxiosRequestConfig, AxiosResponse, AxiosError, AxiosHeaders } from 'axios';

interface MechanicResponse {
  id: string;
  business_name: string;
  mechanics?: Array<{
    id: string;
    business_name: string;
    [key: string]: any;
  }>;
  [key: string]: any;
}

// Environment-aware base URL
const getBaseUrl = () => {
  if (typeof window === 'undefined') {
    // Server-side
    return process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001';
  }
  // Client-side
  return '';  // Use relative URLs on client side
};

// Create axios instance with custom config
const axiosInstance = axios.create({
  // Remove /api from baseURL - we'll handle it in the interceptor
  baseURL: process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Add a request interceptor for logging and URL handling
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (config.url) {
      // Clean up any double slashes and remove any existing api prefix
      config.url = config.url.replace(/\/+/g, '/').replace(/^\/api\/+/, '');
      
      // Add single api prefix
      config.url = `api/${config.url}`;
    }
    
    // Log the final request URL for debugging
    console.log(`Making request to: ${config.baseURL}/${config.url}`, {
      method: config.method,
      headers: config.headers
    });
    
    return config;
  },
  (error: AxiosError) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add a response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response: AxiosResponse<MechanicResponse>) => {
    // Log successful responses for debugging
    console.log(`Response from ${response.config.url}:`, {
      status: response.status,
      contentType: response.headers['content-type']
    });
    
    // Check if response is HTML instead of JSON
    const contentType = response.headers['content-type'];
    if (contentType && contentType.includes('text/html')) {
      console.error('Received HTML response instead of JSON');
      return Promise.reject(new Error('Invalid response type: expected JSON, got HTML'));
    }
    
    return response;
  },
  async (error: AxiosError) => {
    console.error('Request failed:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      message: error.message,
      data: error.response?.data
    });
    
    return Promise.reject(error);
  }
);

export default axiosInstance; 